/**
 * Centralized WebSocket Client
 * Eliminates code duplication across WebSocket implementations
 * Provides consistent WebSocket connection management
 */

import { API_CONFIG } from '@/config/envConfig';

export interface WebSocketMessage {
  type: string;
  id?: string;
  payload?: any;
}

export interface WebSocketSubscription {
  id: string;
  query: string;
  variables?: Record<string, any>;
  onData?: (data: any) => void;
  onError?: (error: any) => void;
}

export interface WebSocketConnectionOptions {
  hasuraToken: string;
  protocol?: string | string[];
  timeout?: number;
  heartbeatInterval?: number;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: any) => void;
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private subscriptions: Map<string, WebSocketSubscription> = new Map();
  private isConnected = false;
  private connectionTimeout: NodeJS.Timeout | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private options: WebSocketConnectionOptions;

  constructor(options: WebSocketConnectionOptions) {
    this.options = options;
  }

  /**
   * Connect to WebSocket server
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      const protocol = this.options.protocol || ['graphql-transport-ws'];
      this.ws = new WebSocket(API_CONFIG.GRAPHQL_WEBSOCKET_URL, protocol);

      // Set connection timeout
      this.connectionTimeout = setTimeout(() => {
        if (!this.isConnected) {
          this.ws?.close();
          reject(new Error('WebSocket connection timeout'));
        }
      }, this.options.timeout || 10000);

      this.ws.onopen = () => {
        console.log('WebSocket connection opened');
        this.ws!.send(JSON.stringify({
          type: 'connection_init',
          payload: {
            headers: {
              Authorization: `Bearer ${this.options.hasuraToken}`,
              'X-Hasura-Role': 'user'
            }
          }
        }));
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);

          if (message.type === 'connection_ack' && !this.isConnected) {
            this.isConnected = true;
            if (this.connectionTimeout) {
              clearTimeout(this.connectionTimeout);
              this.connectionTimeout = null;
            }
            this.startHeartbeat();
            this.options.onConnect?.();
            resolve();
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket connection closed:', event.code, event.reason);
        this.isConnected = false;
        this.cleanup();
        this.options.onDisconnect?.();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.options.onError?.(error);
        if (!this.isConnected) {
          reject(error);
        }
      };
    });
  }

  /**
   * Subscribe to a GraphQL subscription
   */
  subscribe(subscription: WebSocketSubscription): void {
    if (!this.isConnected || !this.ws) {
      throw new Error('WebSocket not connected');
    }

    this.subscriptions.set(subscription.id, subscription);

    this.ws.send(JSON.stringify({
      id: subscription.id,
      type: 'start',
      payload: {
        query: subscription.query,
        variables: subscription.variables || {}
      }
    }));
  }

  /**
   * Unsubscribe from a subscription
   */
  unsubscribe(subscriptionId: string): void {
    if (this.ws && this.isConnected) {
      this.ws.send(JSON.stringify({
        id: subscriptionId,
        type: 'stop'
      }));
    }
    this.subscriptions.delete(subscriptionId);
  }

  /**
   * Close WebSocket connection
   */
  close(): void {
    this.cleanup();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case 'next':
        if (message.id && message.payload?.data) {
          const subscription = this.subscriptions.get(message.id);
          subscription?.onData?.(message.payload.data);
        }
        break;
      
      case 'error':
        if (message.id) {
          const subscription = this.subscriptions.get(message.id);
          subscription?.onError?.(message.payload);
        }
        console.error('WebSocket subscription error:', message.payload);
        break;
      
      case 'complete':
        if (message.id) {
          this.subscriptions.delete(message.id);
        }
        break;
    }
  }

  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    if (this.options.heartbeatInterval && this.options.heartbeatInterval > 0) {
      this.heartbeatInterval = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, this.options.heartbeatInterval);
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    this.subscriptions.clear();
    this.isConnected = false;
  }

  /**
   * Get connection status
   */
  get connected(): boolean {
    return this.isConnected && this.ws?.readyState === WebSocket.OPEN;
  }
}

/**
 * WebSocket Factory for creating configured WebSocket clients
 */
export class WebSocketFactory {
  /**
   * Create a WebSocket client for transfer subscriptions
   */
  static createTransferClient(
    hasuraToken: string,
    onTransferComplete: (careSpecialist: string) => void,
    onError?: (error: string) => void
  ): WebSocketClient {
    return new WebSocketClient({
      hasuraToken,
      protocol: ['graphql-transport-ws'],
      timeout: 10000,
      heartbeatInterval: 30000,
      onError: (error) => {
        console.error('Transfer WebSocket error:', error);
        onError?.('WebSocket connection error');
      }
    });
  }

  /**
   * Create a WebSocket client for notifications
   */
  static createNotificationClient(
    hasuraToken: string,
    onNotification: (data: any) => void,
    onError?: (error: any) => void
  ): WebSocketClient {
    return new WebSocketClient({
      hasuraToken,
      protocol: 'graphql-ws',
      timeout: 10000,
      onError
    });
  }
}

export default WebSocketClient;